import { useEffect, useState, useCallback } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db, auth } from "../services/firebase";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";

const RegisterCompany = () => {
  const [view, setView] = useState("register");
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [address, setAddress] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [stateName, setStateName] = useState("");
  const [pincode, setPincode] = useState("");
  const [email, setEmail] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [toAddress, setToAddress] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [editId, setEditId] = useState(null);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [signatureFile, setSignatureFile] = useState(null);
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifsc, setIfsc] = useState("");

  const fetchAddresses = useCallback(async () => {
    if (!user) return;

    setAddressLoading(true);
    try {
      const snapshot = await getDocs(
        collection(db, "users", user.uid, "toAddresses"),
      );

      setAddresses(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    } catch (err) {
      console.error(err);
    }
    setAddressLoading(false);
  }, [user]);

  const handleSaveAddress = async () => {
    if (!toAddress.trim()) {
      alert("Please enter address");
      return;
    }

    try {
      if (editId) {
        // UPDATE
        await updateDoc(doc(db, "users", user.uid, "toAddresses", editId), {
          address: toAddress,
          updatedAt: new Date(),
        });

        alert("Address updated");
        setEditId(null);
      } else {
        // CREATE
        await addDoc(collection(db, "users", user.uid, "toAddresses"), {
          address: toAddress,
          createdAt: new Date(),
        });

        alert("Address added");
      }

      setToAddress("");
      fetchAddresses();
    } catch (err) {
      console.error(err);
    }
  };

  const confirmDeleteAddress = (addr) => {
    setSelectedAddress(addr);
    setShowAddressConfirm(true);
  };

  const handleDeleteAddress = async () => {
    if (!selectedAddress) return;

    await deleteDoc(
      doc(db, "users", user.uid, "toAddresses", selectedAddress.id),
    );

    setAddresses((prev) => prev.filter((a) => a.id !== selectedAddress.id));
    setShowAddressConfirm(false);
    setSelectedAddress(null);
  };

  const handleEditAddress = (addr) => {
    setToAddress(addr.address);
    setEditId(addr.id);
    setView("address");
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleRegisterCompany = async () => {
    if (!user) {
      alert("User not authenticated");
      return;
    }

    if (
      !companyName ||
      !companyType ||
      !address ||
      !gstNumber ||
      !mobile ||
      !stateName ||
      !pincode ||
      !email ||
      !bankName ||
      !accountNumber ||
      !ifsc
    ) {
      alert("Please enter all fields");
      return;
    }

    if (!signatureFile) {
      alert("Please upload company signature");
      return;
    }

    if (signatureFile.size > 1024 * 1024) {
      alert("Signature must be less than 2MB");
      return;
    }

    try {
      const signatureBase64 = await convertToBase64(signatureFile);
      await addDoc(collection(db, "users", user.uid, "companies"), {
        CompanyName: companyName,
        Type: companyType,
        Address: address,
        GSTNumber: gstNumber,
        Mobile: mobile,
        State: stateName,
        Pincode: pincode,
        Email: email,
        BankName: bankName,
        AccountNumber: accountNumber,
        IFSC: ifsc,
        signatureBase64: signatureBase64,
        createdAt: new Date(),
      });

      setCompanyName("");
      setCompanyType("");
      setAddress("");
      setGstNumber("");
      setMobile("");
      setStateName("");
      setPincode("");
      setEmail("");
      setBankName("");
      setAccountNumber("");
      setIfsc("");
      setSignatureFile(null);

      alert("Company registered successfully");
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCompanies = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const snapshot = await getDocs(
        collection(db, "users", user.uid, "companies"),
      );

      setCompanies(
        snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })),
      );
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  }, [user]);

  const confirmDelete = (company) => {
    setSelectedCompany(company);
    setShowConfirm(true);
  };

  const handleDeleteCompany = async () => {
    await deleteDoc(
      doc(db, "users", user.uid, "companies", selectedCompany.id),
    );

    setCompanies((prev) => prev.filter((c) => c.id !== selectedCompany.id));
    setShowConfirm(false);
  };

  useEffect(() => {
    if (view === "view") fetchCompanies();
    if (view === "address") fetchAddresses();
  }, [view, fetchCompanies, fetchAddresses]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.pageTitle}>Company Management</h2>
          <p style={styles.subTitle}>Register & manage your companies</p>
        </div>

        <button style={styles.backBtn} onClick={() => navigate("/home")}>
          ← Back to Home
        </button>
      </div>

      {/* TOGGLE BUTTONS */}
      <div style={styles.toggle}>
        <button
          style={view === "register" ? styles.activeBtn : styles.secondaryBtn}
          onClick={() => setView("register")}
        >
          Register Company
        </button>

        <button
          style={view === "view" ? styles.activeBtn : styles.secondaryBtn}
          onClick={() => setView("view")}
        >
          View Companies
        </button>
        <button
          style={view === "address" ? styles.activeBtn : styles.secondaryBtn}
          onClick={() => setView("address")}
        >
          Manage To Address
        </button>
      </div>

      {/* REGISTER FORM */}
      {view === "register" && (
        <div style={styles.card}>
          {/* ROW 1 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Company Name</label>
              <input
                style={styles.input}
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Company Type</label>
              <input
                style={styles.input}
                value={companyType}
                onChange={(e) => setCompanyType(e.target.value)}
              />
            </div>
          </div>

          {/* ROW 2 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Address</label>
              <input
                style={styles.input}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>GST Number</label>
              <input
                style={styles.input}
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value)}
              />
            </div>
          </div>

          {/* ROW 3 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Mobile No</label>
              <input
                style={styles.input}
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>State</label>
              <input
                style={styles.input}
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
              />
            </div>
          </div>

          {/* ROW 4 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Pincode</label>
              <input
                style={styles.input}
                type="number"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          {/* ROW 5 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Bank Name</label>
              <input
                style={styles.input}
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>A/C Number</label>
              <input
                style={styles.input}
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
          </div>

          {/* ROW 6 */}
          <div style={styles.formRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>IFSC Code</label>
              <input
                style={styles.input}
                value={ifsc}
                onChange={(e) => setIfsc(e.target.value)}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Upload Digital Signature</label>
              <input
                type="file"
                accept="image/png,image/jpeg"
                style={styles.input}
                onChange={(e) => setSignatureFile(e.target.files[0])}
              />
            </div>
          </div>

          <button style={styles.primaryBtn} onClick={handleRegisterCompany}>
            Register Company
          </button>
        </div>
      )}

      {/* VIEW COMPANIES */}
      {view === "view" && (
        <div style={styles.card}>
          {loading ? (
            <p>Loading companies...</p>
          ) : companies.length === 0 ? (
            <p>No companies registered</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Company Name</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Mobile</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company.id}>
                    <td style={styles.td}>{company.CompanyName}</td>
                    <td style={styles.td}>{company.Type}</td>
                    <td style={styles.td}>{company.Mobile}</td>
                    <td style={styles.td}>{company.Email}</td>
                    <td style={styles.td}>{company.State}</td>
                    <td style={styles.td}>
                      <DeleteIcon
                        style={styles.deleteIcon}
                        onClick={() => confirmDelete(company)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* MANAGE TO ADDRESS */}
      {view === "address" && (
        <div style={styles.card}>
          {/* INPUT */}
          <label style={styles.label}>To Address</label>
          <textarea
            style={{ ...styles.input, minHeight: 100 }}
            value={toAddress}
            onChange={(e) => setToAddress(e.target.value)}
            placeholder="Enter To Address"
          />

          <button style={styles.primaryBtn} onClick={handleSaveAddress}>
            {editId ? "Update Address" : "Add Address"}
          </button>

          {/* TABLE BELOW INPUT */}
          <div style={{ marginTop: 30 }}>
            {addressLoading ? (
              <p>Loading addresses...</p>
            ) : addresses.length === 0 ? (
              <p>No addresses found</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Address</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {addresses.map((addr) => (
                    <tr key={addr.id}>
                      <td style={styles.td}>{addr.address}</td>
                      <td style={styles.td}>
                        <EditIcon
                          style={{
                            ...styles.deleteIcon,
                            color: "#2563eb",
                            marginRight: 10,
                          }}
                          onClick={() => handleEditAddress(addr)}
                        />
                        <DeleteIcon
                          style={styles.deleteIcon}
                          onClick={() => confirmDeleteAddress(addr)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* CONFIRM MODAL */}
      {showConfirm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Delete Company</h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedCompany?.CompanyName}</strong>?
            </p>

            <div style={styles.modalActions}>
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button style={styles.deleteBtn} onClick={handleDeleteCompany}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADDRESS DELETE CONFIRM MODAL */}
      {showAddressConfirm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3>Delete Address</h3>
            <p>Are you sure you want to delete this address?</p>

            <div style={styles.modalActions}>
              <button
                style={styles.secondaryBtn}
                onClick={() => setShowAddressConfirm(false)}
              >
                Cancel
              </button>
              <button style={styles.deleteBtn} onClick={handleDeleteAddress}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    padding: "32px",
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f8fafc, #f1f5f9)",
    fontFamily: "Inter, system-ui",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginBottom: "4px",
  },

  fieldGroup: {
    display: "flex",
    flexDirection: "column",
  },

  pageTitle: {
    fontSize: "26px",
    fontWeight: 700,
    color: "#2563eb",
  },

  subTitle: {
    fontSize: "14px",
    color: "#64748b",
    marginBottom: "28px",
  },

  toggle: {
    display: "flex",
    gap: "12px",
    marginBottom: "24px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "4px",
  },

  backBtn: {
    padding: "10px 16px",
    background: "#1d4ed8",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
  },

  card: {
    background: "#ffffff",
    padding: "26px",
    borderRadius: "18px",
    boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
  },

  label: {
    fontWeight: 600,
    marginBottom: "6px",
    display: "block",
    fontSize: "14px",
  },

  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "18px",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
  },

  primaryBtn: {
    width: "100%",
    padding: "14px",
    background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 10px 20px rgba(37,99,235,0.35)",
  },

  secondaryBtn: {
    padding: "10px 16px",
    background: "#e5e7eb",
    borderRadius: "10px",
    border: "none",
    cursor: "pointer",
  },

  activeBtn: {
    padding: "10px 16px",
    background: "#2563eb",
    color: "#fff",
    borderRadius: "10px",
    border: "none",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 12px",
    marginTop: "10px",
    textAlign: "center",
  },

  th: {
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
  },

  td: {
    padding: "14px",
    background: "#f8fafc",
    borderRadius: "12px",
    fontSize: "14px",
  },

  deleteIcon: {
    color: "#ef4444",
    cursor: "pointer",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  modal: {
    background: "#fff",
    padding: "26px",
    borderRadius: "18px",
    width: "320px",
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
  },

  deleteBtn: {
    padding: "10px 16px",
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
  },
};

export default RegisterCompany;
